import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { User } from '@models/user.model';
import { AuthService, BreakpointService, ContragentRefService, ShoppingApiService } from '@services';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { CardTypes } from '@shared/enums/auth.enums';
import { isNullish } from '@shared/utils/helpers.util';
import { LocalStorageService } from 'ngx-webstorage';
import { Subject, takeUntil } from 'rxjs';
import { AdBannerMediaQueryThreshold, AdBannerStorageKey } from './banner.constants';
import {
  ENovaplaceBannerFormatType,
  ENovaplaceClientType,
  INovaplaceBanner,
  INovaplaceBusinessActionPayload,
  INovaplaceBusinessClosePayload,
  INovaplaceBusinessContactsPayload,
} from './banner.models';

@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent extends Unsubscriber implements OnInit, OnDestroy {
  readonly BannerMediaQueryThreshold = AdBannerMediaQueryThreshold;
  private destroy$: Subject<void> = new Subject<void>();
  private clientType: ENovaplaceClientType;
  private images: INovaplaceBanner[];
  isBannerVisible: boolean = false;
  isBannerListLoaded: boolean = false;
  // Due to media query, we might have more than 1 banner loaded, but only 1 visible.
  // In this case, we should send business focus/action/close only for visible banner,
  // not all loaded banners
  singleBanner: boolean = false;

  constructor(
    private storage: LocalStorageService,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    private adApi: ShoppingApiService,
    private breakpointService: BreakpointService,
    private refsService: ContragentRefService,
  ) {
    super();
  }

  get user(): User {
    return this.authService.user;
  }

  get adBannerStorageKey(): string {
    return `${this.user?.userLogin}_${AdBannerStorageKey}`;
  }

  get displayedImages(): INovaplaceBanner[] {
    if (!this.images) {
      return [];
    }
    return this.singleBanner ? this.images.slice(0, 1) : this.images;
  }

  businessAction(campaignId: string): void {
    const clientPayload = this.getClientPayload();

    const businessActionPayload: INovaplaceBusinessActionPayload = {
      ...clientPayload,
      campaignId,
    };

    this.adApi.businessAction(businessActionPayload).subscribe();
  }

  businessClose(campaignId: string): void {
    this.updateBannerSessionVisibility(false);
    const clientPayload = this.getClientPayload();

    const businessClosePayload: INovaplaceBusinessClosePayload = {
      ...clientPayload,
      campaignId,
    };

    this.adApi.businessClose(businessClosePayload).subscribe();
  }

  businessFocus(campaignId: string): void {
    const clientPayload = this.getClientPayload();

    // Important
    // Call business focus for all DISPLAYED banners
    // As not all loaded banners might be visible due to media threshold

    this.adApi.businessFocus({
      ...clientPayload,
      campaignId,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  bannerImageError(): void {
    this.hideBannerUntilPageReload();

    this.cd.detectChanges();
  }

  ngOnInit(): void {
    this.retrieveBannerVisibility();
    this.setClientType();
    this.initResizeObserver();
    this.getBanners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private hideBannerUntilPageReload(): void {
    this.isBannerVisible = false;
  }

  private retrieveBannerVisibility(): void {
    this.isBannerVisible = isNullish(this.storage.retrieve(this.adBannerStorageKey));
  }

  private updateBannerSessionVisibility(visible: boolean): void {
    this.storage.store(this.adBannerStorageKey, visible);
    this.retrieveBannerVisibility();
  }

  private setClientType(): void {
    this.clientType = this.authService.clientType;
  }

  private getClientPayload(): INovaplaceBusinessContactsPayload {
    return {
      clientType: this.clientType,
      ...(
        this.clientType === ENovaplaceClientType.Corporate
          ? {
            counterpartyRefs: this.refsService.getSelectedContragentRefs(),
            userLogin: this.user.userLogin,
          } : {})
      ,
    };
  }

  private getBanners(): void {
    const clientPayload = this.getClientPayload();

    this.adApi.businessContacts(clientPayload)
      .subscribe({
        next: response => {
          const banners = response?.result?.contacts || [];
          if (!this.bannerListValid(banners)) {
            this.hideBannerUntilPageReload();
            return;
          }

          this.images = banners || [];
          this.isBannerListLoaded = true;

          this.cd.detectChanges();
        },
        error: () => this.hideBannerUntilPageReload(),
      });
  }

  private bannerListValid(banners: INovaplaceBanner[]): boolean {
    if (!banners.length) {
      return false;
    }

    if (banners.length === 1) {
      const banner = banners[0];

      return banner.formatType === ENovaplaceBannerFormatType.B2B_SUPER_BANNER;
    }

    return banners.every(b => this.bannerObjectValid(b));
  }

  private bannerObjectValid(banner: INovaplaceBanner): boolean {
    return !!banner && !!banner.link && !!banner.image && !!banner.campaignId && !!banner.formatType;
  }

  private initResizeObserver(): void {
    this.subscriptions = this.breakpointService.observe(AdBannerMediaQueryThreshold)
      .subscribe(singleBanner => {
        this.singleBanner = singleBanner;
        this.cd.detectChanges();
      });
  }
}
